import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, AlertCircle, FileText, Save, Loader2, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import type { Json } from '@/integrations/supabase/types';

interface EligibilityCriterion {
  key: string;
  label: string;
}

interface EligibilitySchema {
  type: 'merit' | 'humanitarian' | 'general';
  category?: string;
  criteria?: EligibilityCriterion[];
  min_criteria?: number;
  narrative_required?: boolean;
  narrative_label?: string;
  evidence_types?: Array<{ key: string; label: string }>;
  additional_fields?: Array<{
    key: string;
    label: string;
    type: string;
    required?: boolean;
    options?: string[];
  }>;
}

interface CriterionMet {
  key: string;
  evidence: string;
}

interface EligibilityData {
  criteria_met?: CriterionMet[];
  narrative?: string;
  evidence_collected?: string[];
  [key: string]: any;
}

interface OnboardingEligibilityReviewProps {
  itemId: string;
  catalogId: string;
  currentEligibility?: Record<string, any>;
  readonly?: boolean;
  onSave?: () => void;
}

export default function OnboardingEligibilityReview({
  itemId,
  catalogId,
  currentEligibility,
  readonly = false,
  onSave,
}: OnboardingEligibilityReviewProps) {
  const queryClient = useQueryClient();
  const [schema, setSchema] = useState<EligibilitySchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<EligibilityData>({});

  // Carregar schema do catálogo
  useEffect(() => {
    const loadSchema = async () => {
      const { data, error } = await supabase
        .from('catalog')
        .select('eligibility_schema')
        .eq('id', catalogId)
        .single();

      if (error) {
        console.error('Error loading schema:', error);
        setLoading(false);
        return;
      }

      setSchema(data?.eligibility_schema as unknown as EligibilitySchema);
      setLoading(false);
    };

    loadSchema();
  }, [catalogId]);

  // Inicializar formData com valores existentes
  useEffect(() => {
    if (currentEligibility) {
      setFormData(currentEligibility);
    }
  }, [currentEligibility]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('items')
        .update({ eligibility: formData as unknown as Json })
        .eq('id', itemId);

      if (error) throw error;

      toast.success('Elegibilidade salva');
      queryClient.invalidateQueries({ queryKey: ['onboarding-item', itemId] });
      setIsEditing(false);
      onSave?.();
    } catch (error) {
      console.error('Error saving eligibility:', error);
      toast.error('Erro ao salvar elegibilidade');
    } finally {
      setSaving(false);
    }
  };

  // Handlers para critérios (Merit)
  const handleCriterionToggle = (criterion: EligibilityCriterion, checked: boolean) => {
    const current = formData.criteria_met || [];
    if (checked) {
      setFormData({
        ...formData,
        criteria_met: [...current, { key: criterion.key, evidence: '' }],
      });
    } else {
      setFormData({
        ...formData,
        criteria_met: current.filter((c) => c.key !== criterion.key),
      });
    }
  };

  const handleCriterionEvidence = (key: string, evidence: string) => {
    const current = formData.criteria_met || [];
    const updated = current.map((c) => (c.key === key ? { ...c, evidence } : c));
    setFormData({ ...formData, criteria_met: updated });
  };

  const isCriterionMet = (key: string) => {
    return (formData.criteria_met || []).some((c) => c.key === key);
  };

  const getCriterionEvidence = (key: string) => {
    return (formData.criteria_met || []).find((c) => c.key === key)?.evidence || '';
  };

  // Validação
  const getValidation = () => {
    if (!schema) return { valid: true, issues: [] };

    const issues: string[] = [];

    if (schema.type === 'merit' && schema.criteria && schema.min_criteria) {
      const metCount = (formData.criteria_met || []).length;
      if (metCount < schema.min_criteria) {
        issues.push(`Mínimo de ${schema.min_criteria} critérios (${metCount} selecionados)`);
      }
    }

    if (schema.narrative_required && !formData.narrative?.trim()) {
      issues.push('Narrativa obrigatória');
    }

    return { valid: issues.length === 0, issues };
  };

  const validation = getValidation();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 text-muted-foreground">
          <FileText className="w-4 h-4" />
          <span className="text-sm">Este tipo de serviço não requer elegibilidade</span>
        </div>
      </div>
    );
  }

  // Modo visualização
  if (!isEditing && !readonly) {
    return (
      <div className="space-y-3">
        {/* Header com status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {schema.category || schema.type}
            </Badge>
            {validation.valid ? (
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0 text-xs gap-1">
                <CheckCircle className="w-3 h-3" />
                Completo
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-xs gap-1">
                <AlertCircle className="w-3 h-3" />
                Incompleto
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-8 gap-1"
          >
            <Edit2 className="w-3.5 h-3.5" />
            Editar
          </Button>
        </div>

        {/* Conteúdo resumido */}
        {schema.type === 'merit' && schema.criteria && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              Critérios: {(formData.criteria_met || []).length}/{schema.min_criteria || 0} mínimo
            </div>
            <div className="flex flex-wrap gap-1.5">
              {schema.criteria.map((criterion) => (
                <Badge
                  key={criterion.key}
                  variant={isCriterionMet(criterion.key) ? 'default' : 'outline'}
                  className={
                    isCriterionMet(criterion.key)
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0'
                      : 'text-muted-foreground'
                  }
                >
                  {isCriterionMet(criterion.key) && <CheckCircle className="w-3 h-3 mr-1" />}
                  {criterion.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Narrativa (Humanitarian) */}
        {schema.type !== 'merit' && formData.narrative && (
          <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-4">
              {formData.narrative}
            </p>
          </div>
        )}

        {/* Pendências */}
        {!validation.valid && (
          <div className="p-2 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 rounded text-xs">
            Pendências: {validation.issues.join(', ')}
          </div>
        )}
      </div>
    );
  }

  // Modo edição
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {schema.category || schema.type}
          </Badge>
          {validation.valid ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0 text-xs gap-1">
              <CheckCircle className="w-3 h-3" />
              Completo
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-xs gap-1">
              <AlertCircle className="w-3 h-3" />
              Incompleto
            </Badge>
          )}
        </div>
      </div>

      {/* Validation Issues */}
      {!validation.valid && (
        <div className="p-2 bg-destructive/10 text-destructive rounded text-xs">
          <span className="font-medium">Pendências:</span> {validation.issues.join(', ')}
        </div>
      )}

      {/* Merit Criteria */}
      {schema.type === 'merit' && schema.criteria && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Critérios Atendidos</span>
            <span className="text-xs text-muted-foreground">
              {(formData.criteria_met || []).length} / {schema.min_criteria} mínimo
            </span>
          </div>

          <div className="space-y-2">
            {schema.criteria.map((criterion) => (
              <div key={criterion.key} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isCriterionMet(criterion.key)}
                    onCheckedChange={(checked) =>
                      handleCriterionToggle(criterion, checked as boolean)
                    }
                    disabled={readonly}
                  />
                  <span
                    className={
                      isCriterionMet(criterion.key) ? 'text-sm font-medium' : 'text-sm'
                    }
                  >
                    {criterion.label}
                  </span>
                </div>

                {isCriterionMet(criterion.key) && !readonly && (
                  <Textarea
                    value={getCriterionEvidence(criterion.key)}
                    onChange={(e) => handleCriterionEvidence(criterion.key, e.target.value)}
                    placeholder="Evidência para este critério..."
                    className="ml-6 text-sm"
                    rows={2}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Humanitarian Narrative */}
      {schema.type !== 'merit' && schema.narrative_required && (
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {schema.narrative_label || 'Narrativa'}
            <span className="text-destructive ml-1">*</span>
          </label>
          <Textarea
            value={formData.narrative || ''}
            onChange={(e) => setFormData({ ...formData, narrative: e.target.value })}
            placeholder="Descreva a situação do cliente..."
            rows={4}
            disabled={readonly}
          />
        </div>
      )}

      {/* Evidence Types (Humanitarian) */}
      {schema.type !== 'merit' && schema.evidence_types && schema.evidence_types.length > 0 && (
        <div className="space-y-2">
          <span className="text-sm font-medium">Evidências Coletadas</span>
          <div className="grid grid-cols-2 gap-2">
            {schema.evidence_types.map((evidence) => (
              <div
                key={evidence.key}
                className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded text-sm"
              >
                <Checkbox
                  checked={(formData.evidence_collected || []).includes(evidence.key)}
                  onCheckedChange={(checked) => {
                    const current = formData.evidence_collected || [];
                    if (checked) {
                      setFormData({
                        ...formData,
                        evidence_collected: [...current, evidence.key],
                      });
                    } else {
                      setFormData({
                        ...formData,
                        evidence_collected: current.filter((e) => e !== evidence.key),
                      });
                    }
                  }}
                  disabled={readonly}
                />
                <span>{evidence.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {!readonly && (
        <div className="flex items-center gap-2 pt-2">
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            Salvar
          </Button>
          {isEditing && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setFormData(currentEligibility || {});
                setIsEditing(false);
              }}
            >
              Cancelar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
